import {
    Box,
    Card,
    CardContent,
    Chip,
    Container,
    Divider,
    LinearProgress,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Paper,
    Stack,
    Typography,
    useTheme,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import React from 'react';
import Grid from '@mui/material/Unstable_Grid2';
import RankHeaderCard from '../components/RankHeaderCard';
import { useNetworkingBinaryTree, useNetworkingRanks } from '../state/NetworkingContext';
import { formatCurrency } from '../../../app.functions';
import { Rank } from '../networking.types';

// Metallic colors for ranks: Bronze, Silver, Gold, Platinum, Diamond
const colors = [
    '#CD7F32', // Bronze
    '#C0C0C0', // Silver
    '#FFD700', // Gold
    '#E5E4E2', // Platinum
    '#B9F2FF', // Diamond (bright blue-white)
];

// Energy effect colors for each rank
const energyEffects = [
    {
        // Bronze - Fire
        gradient:
            'radial-gradient(circle, rgba(255,140,0,0.6) 0%, rgba(255,69,0,0.4) 50%, transparent 100%)',
        glow: 'rgba(255,140,0,0.5)',
        particles: '#FF8C00',
    },
    {
        // Silver - Lightning
        gradient:
            'radial-gradient(circle, rgba(0,191,255,0.6) 0%, rgba(0,100,255,0.4) 50%, transparent 100%)',
        glow: 'rgba(0,191,255,0.5)',
        particles: '#00BFFF',
    },
    {
        // Gold - Golden Rays
        gradient:
            'radial-gradient(circle, rgba(255,215,0,0.7) 0%, rgba(255,165,0,0.5) 50%, transparent 100%)',
        glow: 'rgba(255,215,0,0.6)',
        particles: '#FFD700',
    },
    {
        // Platinum - Purple Nebula
        gradient:
            'radial-gradient(circle, rgba(138,43,226,0.6) 0%, rgba(75,0,130,0.4) 50%, transparent 100%)',
        glow: 'rgba(138,43,226,0.5)',
        particles: '#8A2BE2',
    },
    {
        // Diamond - Rainbow Stars
        gradient:
            'radial-gradient(circle, rgba(185,242,255,0.7) 0%, rgba(138,43,226,0.5) 30%, rgba(255,20,147,0.4) 60%, transparent 100%)',
        glow: 'rgba(185,242,255,0.6)',
        particles: '#B9F2FF',
    },
];

const getQualificationLabel = (key: string): string => {
    switch (key) {
        case 'organizationSubscriptionLevel':
            return 'Organization Subscription Level';
        case 'fiyrpodAthleteCount':
        case 'athleteCount':
            return 'Paid Fiyrpod Athletes';
        case 'marketplaceSales':
            return 'Annual Marketplace Sales';
        default:
            return key;
    }
};

const getQualificationDescription = (key: string, value: number, actual: number): string => {
    switch (key) {
        case 'organizationSubscriptionLevel': {
            const levelMapping: { [key: number]: string } = {
                0: 'None',
                1: 'Bronze',
                2: 'Silver',
                3: 'Gold',
            };
            return `Requires: ${levelMapping[value] || `Level ${value}`} | Your Level: ${
                levelMapping[actual] || `Level ${actual}`
            }`;
        }
        case 'fiyrpodAthleteCount':
        case 'athleteCount':
            return `Requires: ${value} athletes | You have: ${actual} athletes`;
        case 'marketplaceSales':
            return `Requires: ${formatCurrency({
                amount: value,
                currency: 'USD',
            })} | You have: ${formatCurrency({
                amount: actual / 100,
                currency: 'USD',
            })}`;
        default:
            return '';
    }
};

const getProgressPercentage = (key: string, required: number, actual: number): number => {
    if (key === 'marketplaceSales') {
        const requiredCents = required * 100;
        return Math.min(100, (actual / requiredCents) * 100);
    }
    if (required === 0) return 100;
    return Math.min(100, (actual / required) * 100);
};

const meetsQualificationCriteria = (key: string, rank: Rank, actual: number): boolean => {
    if (key === 'marketplaceSales') {
        return (
            actual >=
            rank.qualification_criteria[key as keyof typeof rank.qualification_criteria] * 100
        );
    }
    return actual >= rank.qualification_criteria[key as keyof typeof rank.qualification_criteria];
};

const PartnershipRanksScene = () => {
    const theme = useTheme();
    const [selectedRank, setSelectedRank] = React.useState<Rank>();
    const ranks = useNetworkingRanks();
    const binaryTree = useNetworkingBinaryTree();
    const levelMapping: { [key: number]: string } = {
        0: 'None',
        1: 'Bronze',
        2: 'Silver',
        3: 'Gold',
    };
    const num = binaryTree?.data?.levels?.organizationSubscriptionLevel ?? 0;
    const currentRank = ranks.data.find((r) => r.level === binaryTree?.data?.rank);

    const getActualValue = (key: string): number => {
        if (!binaryTree?.data?.levels) {
            return 0;
        }

        switch (key) {
            case 'fiyrpodAthleteCount':
            case 'athleteCount': {
                return binaryTree.data?.levels?.fiyrpodAthleteCount ?? 0;
            }
            case 'organizationSubscriptionLevel':
                return binaryTree.data?.levels?.organizationSubscriptionLevel ?? 0;
            case 'marketplaceSales':
                return binaryTree.data?.levels?.marketplaceSales ?? 0;
            default:
                return 0;
        }
    };

    // Auto-select current rank on mount
    React.useEffect(() => {
        if (currentRank && !selectedRank) {
            setSelectedRank(currentRank);
        }
    }, [currentRank, selectedRank]);

    // Get the color for the current rank's top border
    const currentRankColor =
        currentRank && binaryTree?.data?.rank !== undefined
            ? colors[ranks.data.findIndex((r) => r.level === binaryTree?.data?.rank)] || colors[0]
            : colors[0];

    return (
        <Container maxWidth={'xl'} sx={{ py: 4 }}>
            <Stack spacing={4}>
                {/* Hero Section - Current Rank */}
                <Paper
                    elevation={8}
                    sx={{
                        p: 5,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1419 100%)`,
                        border: `2px solid ${theme.palette.primary.main}30`,
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: `0 8px 32px rgba(0,0,0,0.5), inset 0 0 100px rgba(0,0,0,0.3)`,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: `linear-gradient(90deg, ${currentRankColor} 0%, ${currentRankColor}CC 100%)`,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Stack spacing={3} sx={{ position: 'relative', zIndex: 1 }}>
                        <Box>
                            <Typography
                                variant="overline"
                                sx={{
                                    mb: 1,
                                    color: 'rgba(255,255,255,0.7)',
                                    letterSpacing: '1px',
                                }}
                            >
                                Your Current Partnership Rank
                            </Typography>
                            <Typography
                                variant="h3"
                                fontWeight="bold"
                                sx={{
                                    color: '#FFFFFF',
                                    textShadow:
                                        '0 0 20px rgba(255,255,255,0.3), 0 2px 4px rgba(0,0,0,0.5)',
                                    letterSpacing: '0.5px',
                                }}
                            >
                                {currentRank?.name ?? 'Not Ranked'}
                            </Typography>
                            {binaryTree?.data?.leadership_rank && (
                                <Chip
                                    label={`Leadership Rank: ${binaryTree.data.leadership_rank}`}
                                    size="small"
                                    sx={{
                                        mt: 1,
                                        backgroundColor: 'rgba(255,255,255,0.1)',
                                        color: '#FFFFFF',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                    }}
                                />
                            )}
                        </Box>
                        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />
                        <Grid container spacing={3}>
                            <Grid xs={12} sm={6} md={4} lg={2.4}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            gutterBottom
                                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                                        >
                                            Organization Subscription Level
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: '#FFFFFF' }}
                                        >
                                            {levelMapping[num]}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid xs={12} sm={6} md={4} lg={2.4}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            gutterBottom
                                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                                        >
                                            1Tul Paid Athlete Count
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: '#FFFFFF' }}
                                        >
                                            {binaryTree?.data?.levels?.athleteCount ?? 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid xs={12} sm={6} md={4} lg={2.4}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            gutterBottom
                                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                                        >
                                            Fiyrpod Paid Athlete Count
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: '#FFFFFF' }}
                                        >
                                            {binaryTree?.data?.levels?.fiyrpodAthleteCount ?? 0}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid xs={12} sm={6} md={4} lg={2.4}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            gutterBottom
                                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                                        >
                                            Total Marketplace Sales
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: '#FFFFFF' }}
                                        >
                                            {formatCurrency({
                                                amount:
                                                    (binaryTree?.data?.levels
                                                        ?.marketplaceSalesTotal ?? 0) / 100,
                                                currency: 'USD',
                                            })}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid xs={12} sm={6} md={4} lg={2.4}>
                                <Card
                                    variant="outlined"
                                    sx={{
                                        height: '100%',
                                        backgroundColor: 'rgba(255,255,255,0.05)',
                                        border: '1px solid rgba(255,255,255,0.1)',
                                        backdropFilter: 'blur(10px)',
                                    }}
                                >
                                    <CardContent>
                                        <Typography
                                            variant="caption"
                                            display="block"
                                            gutterBottom
                                            sx={{ color: 'rgba(255,255,255,0.7)' }}
                                        >
                                            Marketplace Sales (Past 365 Days)
                                        </Typography>
                                        <Typography
                                            variant="h6"
                                            fontWeight="bold"
                                            sx={{ color: '#FFFFFF' }}
                                        >
                                            {formatCurrency({
                                                amount:
                                                    (binaryTree?.data?.levels?.marketplaceSales ??
                                                        0) / 100,
                                                currency: 'USD',
                                            })}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Stack>
                </Paper>

                {/* Rank Selection Section */}
                <Paper
                    elevation={8}
                    sx={{
                        p: 5,
                        borderRadius: 4,
                        background: `linear-gradient(135deg, #0a0e27 0%, #1a1f3a 50%, #0f1419 100%)`,
                        border: `2px solid ${theme.palette.primary.main}30`,
                        position: 'relative',
                        overflow: 'hidden',
                        boxShadow: `0 8px 32px rgba(0,0,0,0.5), inset 0 0 100px rgba(0,0,0,0.3)`,
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: '4px',
                            background: `linear-gradient(90deg, ${colors[0]} 0%, ${colors[1]} 25%, ${colors[2]} 50%, ${colors[3]} 75%, ${colors[4]} 100%)`,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background:
                                'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.03) 0%, transparent 70%)',
                            pointerEvents: 'none',
                        },
                    }}
                >
                    <Stack spacing={4}>
                        {/* Header Section */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                flexWrap: 'wrap',
                            }}
                        >
                            <Box sx={{ flex: 1, minWidth: 200 }}>
                                <Typography
                                    variant="h4"
                                    fontWeight="bold"
                                    gutterBottom
                                    sx={{
                                        color: '#FFFFFF',
                                        textShadow:
                                            '0 0 20px rgba(255,255,255,0.3), 0 2px 4px rgba(0,0,0,0.5)',
                                        letterSpacing: '0.5px',
                                    }}
                                >
                                    Available Partnership Ranks
                                </Typography>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 1,
                                        mt: 1,
                                        color: 'rgba(255,255,255,0.8)',
                                    }}
                                >
                                    <TrendingUpIcon
                                        fontSize="small"
                                        sx={{ color: theme.palette.primary.light }}
                                    />
                                    Click on a rank to view its qualification requirements and track
                                    your progress
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Rank Cards Grid */}
                        <Grid container spacing={4} sx={{ position: 'relative', zIndex: 1 }}>
                            {ranks.data.map((rank, index) => {
                                const effect = energyEffects[index];
                                const isSelected = selectedRank?.name === rank.name;

                                // Badge data for Bronze, Silver, and Gold
                                const getBadges = (
                                    rankIndex: number,
                                ): Array<{
                                    percentage: string;
                                    level: string;
                                    moneyBags: number;
                                }> | null => {
                                    switch (rankIndex) {
                                        case 0: // Bronze
                                            return [
                                                {
                                                    percentage: '25%',
                                                    level: '1st Level',
                                                    moneyBags: 1,
                                                },
                                            ];
                                        case 1: // Silver
                                            return [
                                                {
                                                    percentage: '25%',
                                                    level: '1st Level',
                                                    moneyBags: 1,
                                                },
                                                {
                                                    percentage: '20%',
                                                    level: '2nd Level',
                                                    moneyBags: 2,
                                                },
                                            ];
                                        case 2: // Gold
                                            return [
                                                {
                                                    percentage: '25%',
                                                    level: '1st Level',
                                                    moneyBags: 1,
                                                },
                                                {
                                                    percentage: '20%',
                                                    level: '2nd Level',
                                                    moneyBags: 2,
                                                },
                                                {
                                                    percentage: '15%',
                                                    level: '3rd Level',
                                                    moneyBags: 3,
                                                },
                                            ];
                                        default:
                                            return null;
                                    }
                                };

                                const badges = getBadges(index);

                                return (
                                    <Grid key={rank.uuid} xs={12} sm={6} md={4} lg={2.4}>
                                        <Box
                                            sx={{
                                                position: 'relative',
                                                transform: isSelected ? 'scale(1.08)' : 'scale(1)',
                                                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                                '&:hover': {
                                                    transform: 'scale(1.05)',
                                                },
                                            }}
                                        >
                                            {/* Energy Effect Aura */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: '120%',
                                                    height: '120%',
                                                    background: effect.gradient,
                                                    borderRadius: '50%',
                                                    opacity: isSelected ? 0.6 : 0.3,
                                                    filter: `blur(20px)`,
                                                    transition: 'opacity 0.4s ease',
                                                    zIndex: 0,
                                                    pointerEvents: 'none',
                                                    animation: 'pulse 3s ease-in-out infinite',
                                                    '@keyframes pulse': {
                                                        '0%, 100%': {
                                                            opacity: isSelected ? 0.6 : 0.3,
                                                            transform:
                                                                'translate(-50%, -50%) scale(1)',
                                                        },
                                                        '50%': {
                                                            opacity: isSelected ? 0.8 : 0.4,
                                                            transform:
                                                                'translate(-50%, -50%) scale(1.1)',
                                                        },
                                                    },
                                                }}
                                            />
                                            {/* Glow Effect */}
                                            <Box
                                                sx={{
                                                    position: 'absolute',
                                                    top: '50%',
                                                    left: '50%',
                                                    transform: 'translate(-50%, -50%)',
                                                    width: '100%',
                                                    height: '100%',
                                                    boxShadow: isSelected
                                                        ? `0 0 40px ${effect.glow}, 0 0 80px ${effect.glow}40`
                                                        : `0 0 20px ${effect.glow}40`,
                                                    borderRadius: 2,
                                                    transition: 'box-shadow 0.4s ease',
                                                    zIndex: 1,
                                                    pointerEvents: 'none',
                                                }}
                                            />
                                            {/* Card */}
                                            <Box sx={{ position: 'relative', zIndex: 2 }}>
                                                <RankHeaderCard
                                                    selected={isSelected}
                                                    onClick={() => {
                                                        setSelectedRank(rank);
                                                    }}
                                                    color={colors[index]}
                                                    title={rank.name}
                                                    isChecked={
                                                        binaryTree?.data?.rank === rank.level
                                                    }
                                                />
                                            </Box>
                                            {/* Check Match Badges - Below Card */}
                                            {badges && badges.length > 0 && (
                                                <Box
                                                    sx={{
                                                        mt: 2,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 0.75,
                                                        alignItems: 'center',
                                                        background:
                                                            'linear-gradient(to top, rgba(0,0,0,0.85) 0%, rgba(0,0,0,0.75) 50%, rgba(0,0,0,0.65) 100%)',
                                                        backdropFilter: 'blur(12px)',
                                                        borderRadius: 2,
                                                        border: `2px solid ${colors[index]}60`,
                                                        px: 1.5,
                                                        py: 1.5,
                                                        '@keyframes badgePulse': {
                                                            '0%, 100%': {
                                                                transform: 'scale(1)',
                                                            },
                                                            '50%': {
                                                                transform: 'scale(1.05)',
                                                            },
                                                        },
                                                    }}
                                                >
                                                    {/* Header */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 0.5,
                                                            mb: 0.5,
                                                        }}
                                                    >
                                                        <PaymentsIcon
                                                            sx={{
                                                                fontSize: '1rem',
                                                                color: colors[index],
                                                                filter: 'drop-shadow(0 0 4px rgba(255,255,255,0.5))',
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                color: '#FFFFFF',
                                                                fontWeight: 700,
                                                                fontSize: '0.75rem',
                                                                textTransform: 'uppercase',
                                                                letterSpacing: '1px',
                                                                textShadow:
                                                                    '1px 1px 2px rgba(0,0,0,0.9), 0 0 4px rgba(0,0,0,0.7)',
                                                            }}
                                                        >
                                                            Check Match Bonus
                                                        </Typography>
                                                    </Box>
                                                    {/* Badges */}
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            flexDirection: 'column',
                                                            gap: 0.5,
                                                            width: '100%',
                                                            alignItems: 'center',
                                                        }}
                                                    >
                                                        {badges.map((badge, badgeIndex) => {
                                                            const moneyBagIcons = '💰'.repeat(
                                                                badge.moneyBags,
                                                            );
                                                            return (
                                                                <Chip
                                                                    key={badgeIndex}
                                                                    label={`${moneyBagIcons} ${badge.percentage} ${badge.level}`}
                                                                    size="small"
                                                                    sx={{
                                                                        background: `linear-gradient(135deg, ${colors[index]} 0%, ${colors[index]}CC 50%, ${colors[index]} 100%)`,
                                                                        color: '#FFFFFF',
                                                                        fontWeight: 800,
                                                                        fontSize: '0.85rem',
                                                                        height: 'auto',
                                                                        minHeight: '28px',
                                                                        width: '100%',
                                                                        maxWidth: '200px',
                                                                        animation:
                                                                            'badgePulse 2s ease-in-out infinite',
                                                                        animationDelay: `${
                                                                            badgeIndex * 0.2
                                                                        }s`,
                                                                        boxShadow: `0 4px 12px rgba(0,0,0,0.5), 0 0 16px ${colors[index]}80, 0 0 24px ${colors[index]}70, 0 0 32px ${colors[index]}50, inset 0 0 8px ${colors[index]}40`,
                                                                        '& .MuiChip-label': {
                                                                            px: 1.5,
                                                                            py: 0.75,
                                                                            whiteSpace: 'nowrap',
                                                                            lineHeight: 1.3,
                                                                            textShadow:
                                                                                '2px 2px 4px rgba(0,0,0,0.9), 0 0 6px rgba(0,0,0,0.7), 1px 1px 2px rgba(0,0,0,1)',
                                                                            letterSpacing: '0.5px',
                                                                        },
                                                                        border: `2px solid ${colors[index]}CC`,
                                                                        transition: 'all 0.3s ease',
                                                                        '&:hover': {
                                                                            transform:
                                                                                'scale(1.05)',
                                                                            boxShadow: `0 6px 16px rgba(0,0,0,0.6), 0 0 24px ${colors[index]}AA, 0 0 36px ${colors[index]}90, 0 0 48px ${colors[index]}70, inset 0 0 12px ${colors[index]}60`,
                                                                        },
                                                                    }}
                                                                />
                                                            );
                                                        })}
                                                    </Box>
                                                </Box>
                                            )}
                                        </Box>
                                    </Grid>
                                );
                            })}
                        </Grid>

                        {/* Progress Indicator */}
                        {currentRank && (
                            <Box
                                sx={{
                                    mt: 2,
                                    p: 2,
                                    backgroundColor: `${theme.palette.success.light}15`,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.success.main}30`,
                                }}
                            >
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                >
                                    <CheckCircleIcon fontSize="small" color="success" />
                                    <strong>Current Rank:</strong> {currentRank.name} • Explore
                                    higher ranks to unlock additional benefits
                                </Typography>
                            </Box>
                        )}
                    </Stack>
                </Paper>

                {/* Qualification Criteria Section */}
                {selectedRank && (
                    <Paper elevation={2} sx={{ p: 4, borderRadius: 3 }}>
                        <Stack spacing={3}>
                            <Box>
                                <Typography variant="h5" fontWeight="bold" gutterBottom>
                                    Qualification Requirements for {selectedRank.name}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Review the requirements below to see your progress toward this
                                    rank
                                </Typography>
                            </Box>
                            <Divider />
                            <List sx={{ width: '100%' }}>
                                {Object.keys(selectedRank.qualification_criteria).map(
                                    (key: string) => {
                                        const required =
                                            selectedRank.qualification_criteria[
                                                key as keyof typeof selectedRank.qualification_criteria
                                            ];
                                        const actual = getActualValue(key);
                                        const meets = meetsQualificationCriteria(
                                            key,
                                            selectedRank,
                                            actual,
                                        );
                                        const progress = getProgressPercentage(
                                            key,
                                            required,
                                            actual,
                                        );

                                        return (
                                            <React.Fragment key={key}>
                                                <ListItem
                                                    sx={{
                                                        flexDirection: 'column',
                                                        alignItems: 'stretch',
                                                        py: 3,
                                                        px: 0,
                                                    }}
                                                >
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            mb: 2,
                                                        }}
                                                    >
                                                        <ListItemIcon sx={{ minWidth: 40 }}>
                                                            {meets ? (
                                                                <CheckCircleIcon
                                                                    color="success"
                                                                    fontSize="large"
                                                                />
                                                            ) : (
                                                                <CancelIcon
                                                                    color="error"
                                                                    fontSize="large"
                                                                />
                                                            )}
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Typography
                                                                    variant="h6"
                                                                    fontWeight="medium"
                                                                >
                                                                    {getQualificationLabel(key)}
                                                                </Typography>
                                                            }
                                                            secondary={
                                                                <Typography
                                                                    variant="body2"
                                                                    color="text.secondary"
                                                                    sx={{ mt: 0.5 }}
                                                                >
                                                                    {getQualificationDescription(
                                                                        key,
                                                                        required,
                                                                        actual,
                                                                    )}
                                                                </Typography>
                                                            }
                                                        />
                                                        <Chip
                                                            label={
                                                                meets ? 'Qualified' : 'In Progress'
                                                            }
                                                            color={meets ? 'success' : 'default'}
                                                            size="small"
                                                        />
                                                    </Box>
                                                    <Box sx={{ ml: 7, mr: 1 }}>
                                                        <LinearProgress
                                                            variant="determinate"
                                                            value={progress}
                                                            sx={{
                                                                height: 8,
                                                                borderRadius: 1,
                                                                backgroundColor:
                                                                    theme.palette.grey[200],
                                                                '& .MuiLinearProgress-bar': {
                                                                    backgroundColor: meets
                                                                        ? theme.palette.success.main
                                                                        : theme.palette.primary
                                                                              .main,
                                                                },
                                                            }}
                                                        />
                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{ mt: 0.5, display: 'block' }}
                                                        >
                                                            {progress.toFixed(0)}% Complete
                                                        </Typography>
                                                    </Box>
                                                </ListItem>
                                                <Divider />
                                            </React.Fragment>
                                        );
                                    },
                                )}
                            </List>
                        </Stack>
                    </Paper>
                )}
            </Stack>
        </Container>
    );
};

export default PartnershipRanksScene;
